import React, { useEffect, useState } from "react";
import "../../../../../../css/CashflowModeller2/Cards/Cards.css";
import AddEditButton from "../../Buttons/AddEditButton";
import Input1 from "../../Inputs/Input1";
import Input2 from "../../Inputs/Input2";
import { useCfmContext } from "../../../Context/CfmContext";
import { useContextUpdater } from "../../../Hooks/UseContextUpdater";
import RegularIncomeCardSmall from "../Income/RegularIncomeCardSmall";
import Input3 from "../../Inputs/Input3";
import InvestmentDialogBox from "../../DialogBoxes/InvestmentDialogBox";
import TimelineOptions from "../../../Utils/TimelineOptions";
import ArrowDown from "../../Buttons/ArrowDown";
import Input1Big from "../../Inputs/Input1Big";

/**
 * InvestmentCardSmall is a React functional component that displays a compact investment card UI.
 * 
 * Features:
 * - Shows a list of investments from context, or a placeholder if none exist.
 * - Allows opening an investment dialog for adding/editing investments.
 * - Collapsible card section for better UI/UX.
 * - Displays investment name/type, fund value, and annual contribution fields (read-only).
 * - Integrates with context for state management and updates.
 * - Includes a button to add/edit investments, which is disabled if no details are present.
 * - Renders a RegularIncomeCardSmall component at the end of the card.
 * 
 * @component
 * @returns {JSX.Element} The rendered investment card component.
 */
const InvestmentCardSmall = () => {
  const { state } = useCfmContext();
  const investments =
    state.investments?.length > 0
      ? state.investments
      : [
          {
            name: "",
            amount: "",
            contribution: "",
            isPlaceholder: true,
          },
        ];

  const { updateContextField, addItem } = useContextUpdater("investments");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <div className={`card-wrapper ${isCollapsed ? "collapsed" : ""}`}>
      <div className="card-rows card-header">
        <div className="Title">Investment</div>
        <div className="card-header-actions">
          <div className="relative-button-wrapper">
            <AddEditButton
              onClick={() => setIsDialogOpen(true)}
              isDisabled={state.details?.length === 0}
            />
            {isDialogOpen && (
              <div className="dialog-overlay">
                <InvestmentDialogBox onClose={() => setIsDialogOpen(false)} />
              </div>
            )}
          </div>
          <div
            className={`arrow-toggle ${isCollapsed ? "rotate" : ""}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ArrowDown />
          </div>
        </div>
      </div>
      <div
        className={`card-collapse-container ${isCollapsed ? "collapsed" : ""}`}
      >
        {investments.map((inv, index) => (
          <div key={index} className="card-rows">
            <Input1Big
              label={
                inv?.name && inv?.type
                  ? `${inv.name.split(" ")[0]} - ${inv.type.split(" ")[0]}`
                      .length > 13
                    ? `${inv.name.split(" ")[0]} - ${
                        inv.type.split(" ")[0]
                      }`.slice(0, 13) + ".."
                    : `${inv.name.split(" ")[0]} - ${inv.type.split(" ")[0]}`
                  : "William - ISA"
              }
              // label={inv.name || "Name"}
              value={inv.fundValue?.value}
              type="number"
              currency={true}
              readOnly
              onChange={(val) => updateContextField(index, "amount", val)}
              isDisabled={state.investments?.length === 0}
            />
            <Input1Big
              label="Contribution"
              toolTip="Annual Contribution"
              value={inv.contribution?.value}
              onChange={(val) => updateContextField(index, "contribution", val)}
              isDisabled={state.investments?.length === 0}
              options={TimelineOptions}
              currency={true}
              readOnly
              type="number"
            />
          </div>
        ))}
        <RegularIncomeCardSmall />
      </div>
    </div>
  );
};

export default InvestmentCardSmall;
